import useAbortableData from "@hooks/useAbortableData";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { components } from "@apiSchema";
import { mapCategoryProfileHighlights } from "@apiHooks/useCategoryProfileHighlights";

type FoodProfileInsightsSummaryResponse = components["schemas"]["FoodProfileInsightsSummaryViewModel"];

export interface FoodProfileInsightsSummary {
    chainsCount: number;
    bullets: string[];
}

const REQUEST_TYPE: keyof typeof apiConfig = "get food profile insights summary";

/** Module-level so `useData` gets a stable default between renders. */
const EMPTY_INSIGHTS_SUMMARY: FoodProfileInsightsSummary = { chainsCount: 0, bullets: [] };

export function mapFoodProfileInsightsSummary(
    response?: FoodProfileInsightsSummaryResponse | null,
): FoodProfileInsightsSummary {
    if (!response) return EMPTY_INSIGHTS_SUMMARY;

    return {
        chainsCount: response.chainsCount ?? 0,
        // Same bullet shape as Category Profile: trimmed, empty ones dropped, API order kept.
        bullets: mapCategoryProfileHighlights(response),
    };
}

async function getFoodProfileInsightsSummary(word: string, signal: AbortSignal): Promise<FoodProfileInsightsSummary> {
    const response: FoodProfileInsightsSummaryResponse = await http.get(REQUEST_TYPE, { word }, { signal });
    return mapFoodProfileInsightsSummary(response);
}

/** Insights summary for a food word. Empty on no word, 204, 400 or an unparseable response. */
export default function useFoodProfileInsightsSummary(word: string) {
    return useAbortableData(EMPTY_INSIGHTS_SUMMARY, getFoodProfileInsightsSummary, word, REQUEST_TYPE);
}
